import { Children, useEffect, useRef, useState } from 'react'
import { DWELL_PAGE_MS, PAGE_FADE_MS } from '../config/board'

// A list longer than its slot turns the page instead of shrinking or scrolling: row
// heights never change, so nothing on the wall reflows (build-guide §5). Today every
// list fits and this renders a plain list — it exists so that a category outgrowing
// its slot degrades instead of overflowing.
export function Paged({
  capacity,
  fill = false,
  children,
}: {
  capacity: number
  fill?: boolean
  children: React.ReactNode
}) {
  const items = Children.toArray(children)
  const pageCount = Math.max(1, Math.ceil(items.length / capacity))
  const [index, setIndex] = useState(0)
  const [visible, setVisible] = useState(true)
  const fadeTimer = useRef<number | undefined>(undefined)

  useEffect(() => {
    if (pageCount <= 1) {
      setIndex(0)
      return
    }
    const id = window.setInterval(() => {
      setVisible(false)
      fadeTimer.current = window.setTimeout(() => {
        setIndex((i) => (i + 1) % pageCount)
        setVisible(true)
      }, PAGE_FADE_MS)
    }, DWELL_PAGE_MS)
    return () => {
      window.clearInterval(id)
      if (fadeTimer.current !== undefined) window.clearTimeout(fadeTimer.current)
    }
  }, [pageCount])

  const page = index < pageCount ? index : 0
  const shown = items.slice(page * capacity, page * capacity + capacity)

  // A list asked to `fill` its slot spreads its rows evenly over the height left under
  // it, so the list ends level with the hero panel instead of above an empty band that
  // reads as a cut-off screen on the wall (lessons.md). Only a single page stretches:
  // a short last page would blow two rows up to the whole slot. Row type never changes.
  const stretch = fill && pageCount === 1

  return (
    <div className={'flex flex-col' + (stretch ? ' flex-1 min-h-0' : '')}>
      <div
        className={'flex flex-col' + (stretch ? ' flex-1 min-h-0 [&>*]:flex-1' : '')}
        style={{ opacity: visible ? 1 : 0, transition: 'opacity ' + PAGE_FADE_MS + 'ms linear' }}
      >
        {shown}
      </div>
      {pageCount > 1 ? (
        <div className="flex items-center space-x-[8px] pt-[10px]">
          {Array.from({ length: pageCount }, (_unused, i) => (
            <span
              key={i}
              className={'block h-[14px] rounded-full ' + (i === page ? 'bg-brand-jasper' : 'bg-brand-line')}
              style={{ width: i === page ? 40 : 14 }}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}
